export interface ProfileHistoryRow {
  roundId: string;
  matchId: string;
  matchDayId: string | null;
  courseName: string | null;
  date: string;
  grossScore: number;
  adjustedGrossScore: number;
  netScore: number;
  handicapDifferential: number | null;
  handicapIndex: number;
  playerAbsent: boolean;
}

export interface ProfileTableRow {
  kind: 'starting' | 'round';
  label: string;
  roundId: string | null;
  matchId: string | null;
  matchDayId: string | null;
  date: string | null;
  courseName: string | null;
  grossScore: number | null;
  adjustedGrossScore: number | null;
  netScore: number | null;
  handicapDifferential: number | null;
  handicapIndex: number;
  playerAbsent: boolean;
}

export interface MatchDayLike {
  id: string;
  date: string;
  course_id: string | null;
  status: 'scheduled' | 'completed' | 'locked';
  hammer_hole?: number | null;
  weekNumber?: number;
  week_number?: number;
}

export interface LeagueMemberLike {
  player_id: string;
  player?: {
    name?: string;
  } | null;
}

export interface MatchLike {
  id: string;
  match_day_id: string;
  player_a_id: string;
  player_b_id: string;
  match_date: string;
  status: 'scheduled' | 'completed';
  player_a_points?: number;
  player_b_points?: number;
}

export interface MatchDayGroup {
  id: string;
  date: string;
  weekNumber: number | null;
  courseId: string | null;
  courseName: string;
  hammerHole: number | null;
  status: 'scheduled' | 'completed' | 'locked';
  totalMatches: number;
  completedMatches: number;
  matches: Array<{
    id: string;
    playerAId: string;
    playerBId: string;
    playerAName: string;
    playerBName: string;
    status: 'scheduled' | 'completed';
    playerAPoints: number;
    playerBPoints: number;
  }>;
}

export interface PlayerMatchHistoryRow {
  matchId: string;
  date: string;
  opponentName: string;
  score: string;
  result: 'W' | 'L' | 'T' | '--';
  status: 'scheduled' | 'completed';
}

export function roundToTenth(value: number): number {
  return Math.round(value * 10) / 10;
}

export function buildSeasonProfileTableRows(
  startingHandicap: number,
  history: ProfileHistoryRow[],
): ProfileTableRow[] {
  return [
    {
      kind: 'starting',
      label: 'Starting Handicap',
      roundId: null,
      matchId: null,
      matchDayId: null,
      date: null,
      courseName: null,
      grossScore: null,
      adjustedGrossScore: null,
      netScore: null,
      handicapDifferential: null,
      handicapIndex: roundToTenth(startingHandicap),
      playerAbsent: false,
    },
    ...history.map((row) => ({
      kind: 'round' as const,
      label: row.date,
      roundId: row.roundId,
      matchId: row.matchId,
      matchDayId: row.matchDayId,
      date: row.date,
      courseName: row.courseName,
      grossScore: row.grossScore,
      adjustedGrossScore: row.adjustedGrossScore,
      netScore: row.netScore,
      handicapDifferential:
        row.playerAbsent || row.handicapDifferential === null ? null : roundToTenth(row.handicapDifferential),
      handicapIndex: roundToTenth(row.handicapIndex),
      playerAbsent: row.playerAbsent,
    })),
  ];
}

export interface HandicapRoundLike {
  id: string;
  differential: number;
}

export function selectUsedHandicapRoundIds(rounds: HandicapRoundLike[]): Set<string> {
  const recent = rounds.slice(-5);
  const used = recent.length <= 3
    ? recent
    : [...recent].sort((left, right) => left.differential - right.differential).slice(0, 3);
  return new Set(used.map((round) => round.id));
}

export function describeHandicapCalculation(roundCount: number): string {
  if (roundCount === 0) return 'Your starting handicap applies until your first qualifying round.';
  if (roundCount === 1) return 'After 1 round: (2 × starting handicap + differential) ÷ 3.';
  if (roundCount === 2) return 'After 2 rounds: (starting handicap + both differentials) ÷ 3.';
  if (roundCount === 3) return 'After 3 rounds: average of all 3 differentials.';
  if (roundCount === 4) return 'After 4 rounds: average of the lowest 3 differentials.';
  return 'After 5 or more rounds: average of the lowest 3 differentials from the last 5 qualifying rounds.';
}

function descendingDate(left: string, right: string): number {
  return right.localeCompare(left);
}

function ascendingDate(left: string, right: string): number {
  return left.localeCompare(right);
}

export function buildPlayerNameMap(members: LeagueMemberLike[]): Map<string, string> {
  return new Map(members.map((member) => [member.player_id, member.player?.name || member.player_id]));
}

function resolveMatchResult(playerPoints: number, opponentPoints: number): 'W' | 'L' | 'T' {
  if (playerPoints > opponentPoints) {
    return 'W';
  }
  if (playerPoints < opponentPoints) {
    return 'L';
  }
  return 'T';
}

export function buildPlayerMatchHistoryRows(
  playerId: string,
  matches: MatchLike[],
  playerNameById: Map<string, string>,
  todayIso: string,
): PlayerMatchHistoryRow[] {
  return [...matches]
    .filter(
      (match) =>
        (match.player_a_id === playerId || match.player_b_id === playerId) &&
        (match.status === 'completed' || match.match_date >= todayIso),
    )
    .sort((left, right) => {
      const dateDiff = ascendingDate(left.match_date, right.match_date);
      return dateDiff !== 0 ? dateDiff : left.id.localeCompare(right.id);
    })
    .map((match) => {
      const viewedPlayerIsA = match.player_a_id === playerId;
      const opponentId = viewedPlayerIsA ? match.player_b_id : match.player_a_id;

      if (match.status === 'scheduled') {
        return {
          matchId: match.id,
          date: match.match_date,
          opponentName: playerNameById.get(opponentId) ?? opponentId,
          score: '--',
          result: '--',
          status: match.status,
        };
      }

      const playerPoints = Number(viewedPlayerIsA ? match.player_a_points ?? 0 : match.player_b_points ?? 0);
      const opponentPoints = Number(viewedPlayerIsA ? match.player_b_points ?? 0 : match.player_a_points ?? 0);

      return {
        matchId: match.id,
        date: match.match_date,
        opponentName: playerNameById.get(opponentId) ?? opponentId,
        score: `${playerPoints}-${opponentPoints}`,
        result: resolveMatchResult(playerPoints, opponentPoints),
        status: match.status,
      };
    });
}

export function formatPlayerNameWithHandicap(
  name: string,
  handicapIndex?: number | null,
  courseHandicap?: number | null,
): string {
  if (typeof handicapIndex !== 'number' || Number.isNaN(handicapIndex)) {
    return name;
  }

  const handicapLabel = `HI: ${roundToTenth(handicapIndex).toFixed(1)}`;
  if (typeof courseHandicap !== 'number' || Number.isNaN(courseHandicap)) {
    return `${name} (${handicapLabel})`;
  }

  return `${name} (${handicapLabel} / CH: ${Math.round(courseHandicap)})`;
}

export function buildMatchDayGroups(
  matchDays: MatchDayLike[],
  matches: MatchLike[],
  courseNameById: Map<string, string>,
  playerNameById: Map<string, string>,
): MatchDayGroup[] {
  const matchesByDay = new Map<string, MatchLike[]>();
  for (const match of matches) {
    const existing = matchesByDay.get(match.match_day_id) ?? [];
    existing.push(match);
    matchesByDay.set(match.match_day_id, existing);
  }

  return [...matchDays]
    .sort((left, right) => {
      const dateDiff = descendingDate(left.date, right.date);
      return dateDiff !== 0 ? dateDiff : right.id.localeCompare(left.id);
    })
    .map((matchDay) => {
      const groupedMatches = [...(matchesByDay.get(matchDay.id) ?? [])].sort((left, right) =>
        left.id.localeCompare(right.id),
      );
      return {
        id: matchDay.id,
        date: matchDay.date,
        weekNumber:
          Number.isInteger(matchDay.weekNumber) || Number.isInteger(matchDay.week_number)
            ? Number(matchDay.weekNumber ?? matchDay.week_number)
            : null,
        courseId: matchDay.course_id,
        courseName: matchDay.course_id ? courseNameById.get(matchDay.course_id) ?? matchDay.course_id : 'TBD',
        hammerHole: Number.isInteger(matchDay.hammer_hole) ? Number(matchDay.hammer_hole) : null,
        status: matchDay.status,
        totalMatches: groupedMatches.length,
        completedMatches: groupedMatches.filter((match) => match.status === 'completed').length,
        matches: groupedMatches.map((match) => ({
          id: match.id,
          playerAId: match.player_a_id,
          playerBId: match.player_b_id,
          playerAName: playerNameById.get(match.player_a_id) ?? match.player_a_id,
          playerBName: playerNameById.get(match.player_b_id) ?? match.player_b_id,
          status: match.status,
          playerAPoints: Number(match.player_a_points ?? 0),
          playerBPoints: Number(match.player_b_points ?? 0),
        })),
      };
    });
}

export function selectUpcomingMatchDay(groups: MatchDayGroup[], todayIso: string): MatchDayGroup | null {
  return (
    [...groups]
      .sort((left, right) => {
        const dateDiff = ascendingDate(left.date, right.date);
        return dateDiff !== 0 ? dateDiff : left.id.localeCompare(right.id);
      })
      .find((group) => group.date >= todayIso || group.matches.some((match) => match.status === 'scheduled')) ?? null
  );
}

export function selectLatestCompletedMatchDay(groups: MatchDayGroup[], todayIso: string): MatchDayGroup | null {
  return (
    [...groups]
      .sort((left, right) => {
        const dateDiff = descendingDate(left.date, right.date);
        return dateDiff !== 0 ? dateDiff : right.id.localeCompare(left.id);
      })
      .find((group) => group.date <= todayIso && group.completedMatches > 0) ?? null
  );
}
